import { CancelIcon } from '@chegg-ui/horizon-icons'
import { IconButton } from '@chegg-ui/button'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useRef, useState } from 'react'
import { paraphraseSelectionDataHandler } from '@chegg-wt/core-wa/utils/pusherDataHandlers'
import {
  enabledWAFeaturesSelector,
  paperIdSelector,
  quickActionPanelTypeSelector,
  rewritePanelSentencesSelector,
  sentenceOffsetsSelector,
} from '@chegg-wt/core-wa/state/selectors'
import pusherManager from '@chegg-wt/core-wa/utils/pusherManager'
import {
  ErrorData,
  WtPusherEvent,
  ProofReaderCardActions,
  ToastType,
} from '@chegg-wt/core-wa/state/types'
import useEditorSelection from '@chegg-wt/core-wa/hooks/useEditorSelection'
import getFeatureEditor from '@chegg-wt/core-wa/utils/getEditor'
import {
  emitNotificationEvent,
  userClick,
} from '@chegg-wt/core/state/instrumentation/actions'
import {
  RESULT_MISMATCH_REWRITE_COUNT,
  REWRITE_PANEL_TEST_ID,
  SHIMMER_REWRITE_TEST_ID,
} from '@chegg-wt/core-wa/constants/paraphraser'
import {
  QuickActionLoadingStatus,
  PARAPHRASE_PANEL_TITLE,
  PARAPHRASE_ICON_DATA_TEST,
  EXPAND_ICON_DATA_TEST,
  SHORTEN_ICON_DATA_TEST,
  REWRITE_ERROR_HEADING,
} from '@chegg-wt/core-wa/state/constants'
import { notifyToast } from '@chegg-wt/core-wa/state/actions'
import { QuickActionPanelType } from '@chegg-wt/core-wa/types/quickActions'
import { ThreeDots } from '../ThreeDots'
import { RewriteErrorState } from '../BubbleMenuContainer/ErrorStates'
import { ParaphraseIcon, ExpandIcon, ShortenIcon } from '../icons'

import {
  Header,
  QuickActionRightPanelContainer,
  QuickActionRightPanelDiffText,
  QuickActionRightPanelContent,
  QuickActionRightPanelFooterButtons,
  QuickActionRightPanelHeading,
  QuickActionRightPanelIcons,
  ShimmerQARightPanel,
  TryAgainPanelButton,
  UpdatePanelButton,
} from './styled'
import {
  displaySelectedTextIfQAPanelOpen,
  getWordDiffCount,
  handleApplySuggestion,
  handleThreeDotsClick,
  onCloseQARightPanel,
  sendQuickActionSelectedRioCall,
} from './functions/utils'
import {
  QuickActionRightPanelSubheading,
  isExpandShortenResultMismatch,
} from './functions/subHeadingUtils'
import {
  fetchParaphraseFeedback,
  quickActionMap,
} from './functions/fetchParaphraseFeedback'
import { QuickActionRightPanelProps } from './types'
import { actionOriginsSuggestionClick } from './constants'

const QuickActionRightPanel = ({
  initialLoading = true,
  hasError = null,
}: QuickActionRightPanelProps) => {
  const [originalText, setOriginalText] = useState<string>('')
  const [suggestedText, setSuggestedText] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(initialLoading)
  const [tryAgainRioSent, setTryAgainRioSent] = useState<boolean>(true)
  const [onTryAnotherClick, setOnTryAnotherClick] = useState<boolean>(false)
  const [error, setError] = useState<ErrorData>(hasError)
  const [resultMismatchRetry, setResultMismatchRetry] = useState<number>(
    RESULT_MISMATCH_REWRITE_COUNT
  )

  const dispatch = useDispatch()

  const qaRightPanelSentences = useSelector(rewritePanelSentencesSelector)
  const quickActionPanelType = useSelector(quickActionPanelTypeSelector)
  const sentenceOffsets = useSelector(sentenceOffsetsSelector)
  const paperId = useSelector(paperIdSelector)
  const enabledWAFeaturesSelectorFlag = useSelector(enabledWAFeaturesSelector)
  const isUndoRedoEnabled = enabledWAFeaturesSelectorFlag.redoUndo

  const selection = useEditorSelection()
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>()
  const editor = getFeatureEditor()
  editor.setEditable(false)
  const isQuickActionPanelError = !!error && !isLoading

  useEffect(() => {
    if (isQuickActionPanelError) {
      dispatch(
        emitNotificationEvent({
          type: 'error',
          message_name: 'quick actions unavailable',
          message_detail: REWRITE_ERROR_HEADING,
          element: {
            name: quickActionPanelType.toLocaleLowerCase(),
            type: 'component',
          },
        })
      )
    }
  }, [isQuickActionPanelError, quickActionPanelType])

  useEffect(() => {
    if (error && onTryAnotherClick) {
      dispatch(notifyToast(ToastType.QUICK_ACTION_RETRY_ERROR))
      setOnTryAnotherClick(false)
    }
  }, [error])

  useEffect(() => {
    if (suggestedText && !tryAgainRioSent) {
      dispatch(
        userClick({
          origin: actionOriginsSuggestionClick[quickActionPanelType],
          text: originalText,
          value: suggestedText,
          citationMetadata: {
            proofReaderCardAction: ProofReaderCardActions.REGENERATE,
            ...(quickActionPanelType !== QuickActionPanelType.PARAPHRASE && {
              word_count: getWordDiffCount(
                originalText,
                suggestedText,
                quickActionPanelType
              ),
            }),
          },
        })
      )
      setTryAgainRioSent(true)
    }
  }, [suggestedText, tryAgainRioSent])

  const handleParaphraseCompletion = (originalText: string, suggestedText: string) => {
    setOriginalText(originalText)
    setSuggestedText(suggestedText)
    if (
      quickActionPanelType === QuickActionPanelType.PARAPHRASE ||
      !isExpandShortenResultMismatch({
        originalText,
        suggestedText,
        quickActionPanelType,
      }) ||
      resultMismatchRetry === 0
    ) {
      setIsLoading(false)
      clearTimeout(timeoutIdRef.current)
      setOnTryAnotherClick(false)
    } else {
      setResultMismatchRetry((prevResultMismatchRetry) => prevResultMismatchRetry - 1)
    }
  }

  const pusherCallback = (eventData: WtPusherEvent) =>
    paraphraseSelectionDataHandler.handleParaphraseFeedbackEvent(
      eventData,
      handleParaphraseCompletion,
      setError,
      setIsLoading
    )

  const fetchNewSentences = () => {
    setOriginalText('')
    setSuggestedText('')

    fetchParaphraseFeedback({
      qaRightPanelSentences,
      onSubscribe: pusherCallback,
      setError,
      setIsLoading,
      paperId,
      quickActionType: quickActionPanelType,
    })
  }

  const handleTryAgain = () => {
    setTryAgainRioSent(false)
    fetchNewSentences()
    setOnTryAnotherClick(true)
  }

  useEffect(() => {
    displaySelectedTextIfQAPanelOpen(selection, sentenceOffsets)

    if (qaRightPanelSentences?.length > 0) fetchNewSentences()

    const timeoutId = timeoutIdRef.current
    sendQuickActionSelectedRioCall(
      dispatch,
      selection,
      sentenceOffsets,
      quickActionPanelType
    )
    return () => {
      paraphraseSelectionDataHandler.disableParaphraseFeedbackAcceptance()
      pusherManager.unbind(quickActionMap[quickActionPanelType].channel, pusherCallback)
      clearTimeout(timeoutId)
    }
  }, [qaRightPanelSentences, resultMismatchRetry])

  const getQuickActionHeaderIcon = (quickActionPanelType: QuickActionPanelType) => {
    switch (quickActionPanelType) {
      case QuickActionPanelType.EXPAND:
        return <ExpandIcon data-test={EXPAND_ICON_DATA_TEST} />
      case QuickActionPanelType.SHORTEN:
        return <ShortenIcon data-test={SHORTEN_ICON_DATA_TEST} />
      default:
        return <ParaphraseIcon testId={PARAPHRASE_ICON_DATA_TEST} />
    }
  }

  return (
    <QuickActionRightPanelContainer data-test={`${REWRITE_PANEL_TEST_ID}`}>
      <QuickActionRightPanelHeading>
        {isQuickActionPanelError ? (
          <Header error={isQuickActionPanelError}>
            <span>{REWRITE_ERROR_HEADING}</span>
          </Header>
        ) : (
          <Header>
            {getQuickActionHeaderIcon(quickActionPanelType)}
            <span>
              {isLoading
                ? QuickActionLoadingStatus[quickActionPanelType]
                : PARAPHRASE_PANEL_TITLE}
            </span>
          </Header>
        )}

        <QuickActionRightPanelIcons>
          <IconButton
            size="medium"
            screenReaderOnlyText="Rewrite Close"
            onClick={() => onCloseQARightPanel(dispatch)}
            data-test={`${REWRITE_PANEL_TEST_ID}-close-icon`}
            variant="simple"
          >
            <CancelIcon />
          </IconButton>
        </QuickActionRightPanelIcons>
      </QuickActionRightPanelHeading>
      {!isLoading && !isQuickActionPanelError && (
        <QuickActionRightPanelSubheading
          originalText={originalText}
          suggestedText={suggestedText}
          quickActionPanelType={quickActionPanelType}
        />
      )}
      <QuickActionRightPanelContent
        data-test={`${REWRITE_PANEL_TEST_ID}-content`}
        error={isQuickActionPanelError}
        isUndoRedoEnabled={isUndoRedoEnabled}
      >
        {isQuickActionPanelError && (
          <RewriteErrorState
            handleTryAgain={handleTryAgain}
            quickActionPanelType={quickActionPanelType}
          />
        )}

        {isLoading ? (
          <ShimmerQARightPanel data-test={`${SHIMMER_REWRITE_TEST_ID}`} />
        ) : (
          !isQuickActionPanelError && (
            <QuickActionRightPanelDiffText
              originalText={originalText}
              suggestedText={suggestedText}
            />
          )
        )}
      </QuickActionRightPanelContent>
      <QuickActionRightPanelFooterButtons>
        {!isQuickActionPanelError && (
          <>
            <UpdatePanelButton
              disabled={isLoading}
              onClick={() =>
                handleApplySuggestion(
                  selection,
                  dispatch,
                  suggestedText,
                  quickActionPanelType
                )
              }
            >
              Apply
            </UpdatePanelButton>

            <TryAgainPanelButton disabled={isLoading} onClick={handleTryAgain}>
              Try Another
            </TryAgainPanelButton>
            <ThreeDots
              data-test={`${REWRITE_PANEL_TEST_ID}-three-dots`}
              onFlagClick={() =>
                handleThreeDotsClick(
                  dispatch,
                  originalText,
                  suggestedText,
                  quickActionPanelType
                )
              }
              position="topLeft"
            />
          </>
        )}
      </QuickActionRightPanelFooterButtons>
    </QuickActionRightPanelContainer>
  )
}

export default QuickActionRightPanel
